/**
 * PrimeArtifactPropertyTable Component
 * 
 * Displays a table of prime artifact properties with search, filtering, and action buttons.
 * Extracted from PrimeArtifactPropertiesManager for better separation of concerns.
 */

import React from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Space,
  Button,
  Tag,
  Badge,
  Typography,
  Tooltip,
  Popconfirm
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  ClearOutlined,
  EditOutlined,
  DeleteOutlined,
  LinkOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { PrimeArtifactPropertyRow } from '../../hooks/usePrimeArtifactPropertyTable';
import { RelationshipType } from '../../types/propertyRelationships';
import type { ColumnsType } from 'antd/es/table';

const { Option } = Select;
const { Text } = Typography;

interface PrimeArtifactPropertyTableProps {
  columns: ColumnsType<PrimeArtifactPropertyRow>;
  dataSource: PrimeArtifactPropertyRow[];
  loading: boolean;
  searchText: string;
  filterSource?: string;
  filterType?: string;
  onSearchChange: (value: string) => void;
  onFilterSourceChange: (value?: string) => void;
  onFilterTypeChange: (value?: string) => void;
  onRefresh: () => void;
  onRowClick?: (record: PrimeArtifactPropertyRow) => void;
  onAddProperty: () => void;
  onEditProperty: (property: PrimeArtifactPropertyRow) => void;
  onDeleteProperty: (property: PrimeArtifactPropertyRow) => void;
  selectedPropertyId?: string | null;
  artifactName: string;
  totalProperties: number;
}

export const PrimeArtifactPropertyTable: React.FC<PrimeArtifactPropertyTableProps> = ({
  columns,
  dataSource,
  loading,
  searchText,
  filterSource,
  filterType,
  onSearchChange,
  onFilterSourceChange,
  onFilterTypeChange,
  onRefresh,
  onRowClick,
  onAddProperty,
  onEditProperty,
  onDeleteProperty,
  selectedPropertyId,
  artifactName,
  totalProperties
}) => {
  return (
    <Card
      title={
        <Space>
          <span>Artifact Properties</span>
          <Badge count={totalProperties} />
        </Space>
      }
      extra={
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddProperty}
          >
            Add Property
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
        <Space wrap>
          <Input
            placeholder="Search properties, descriptions, states..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="Filter by Source"
            value={filterSource}
            onChange={onFilterSourceChange}
            allowClear
            style={{ width: 180 }}
          >
            <Option value="user_input">User Input</Option>
            <Option value="system_generated">System Generated</Option>
            <Option value="referenced_artifact">Referenced Artifact</Option>
          </Select>
          <Select
            placeholder="Filter by Type"
            value={filterType}
            onChange={onFilterTypeChange}
            allowClear
            style={{ width: 150 }}
          >
            <Option value="string">String</Option>
            <Option value="number">Number</Option>
            <Option value="boolean">Boolean</Option>
            <Option value="date">Date</Option>
            <Option value="currency">Currency</Option>
            <Option value="duration">Duration</Option>
            <Option value="url">URL</Option>
            <Option value="uuid">UUID</Option>
            <Option value="uuid[]">UUID[]</Option>
            <Option value="list">List</Option>
          </Select>
          {(searchText || filterSource || filterType) && (
            <Button
              icon={<ClearOutlined />}
              onClick={() => {
                onSearchChange('');
                onFilterSourceChange(undefined);
                onFilterTypeChange(undefined);
              }}
            >
              Clear Filters
            </Button>
          )}
        </Space>
        <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
          <Text type="secondary">
            Showing {dataSource.length} of {totalProperties} properties
          </Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag color="geekblue" style={{ fontSize: 10 }}>From Global</Tag>
            = Property definition inherited from global properties
          </Text>
        </Space>
      </Space>

      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} properties`
        }}
        scroll={{ x: 1500 }}
        onRow={onRowClick ? (record) => ({
          onClick: () => onRowClick(record),
          style: {
            cursor: 'pointer',
            backgroundColor: selectedPropertyId === record.id ? '#e6f7ff' : 'transparent'
          }
        }) : undefined}
      />
    </Card>
  );
};
